import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Palette, Sparkles, PenTool } from 'lucide-react';
import {
    APP_FADE,
    MOTION_EASE,
    revealUpVariants,
    sectionStaggerVariants,
    surfaceEntranceVariants,
} from '../utils/motion';

export const LoadingScreen: React.FC = () => {
    const prefersReducedMotion = useReducedMotion();

    return (
        <div className="fixed inset-0 z-50 isolate flex flex-col items-center justify-center overflow-hidden bg-[#F8FAFC] dark:bg-gray-950">
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <motion.div
                    aria-hidden="true"
                    animate={prefersReducedMotion ? { opacity: 0.35 } : { x: [-24, 24, -24], y: [0, -18, 0], opacity: [0.32, 0.5, 0.32] }}
                    transition={prefersReducedMotion ? APP_FADE : { duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute -left-24 top-[-10%] h-80 w-80 rounded-full bg-indigo-400/20 blur-3xl"
                />
                <motion.div
                    aria-hidden="true"
                    animate={prefersReducedMotion ? { opacity: 0.35 } : { x: [18, -28, 18], y: [0, 24, 0], opacity: [0.28, 0.45, 0.28] }}
                    transition={prefersReducedMotion ? APP_FADE : { duration: 12, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute -right-24 bottom-[-12%] h-96 w-96 rounded-full bg-fuchsia-400/16 blur-3xl"
                />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.10),transparent_38%),radial-gradient(circle_at_bottom,rgba(236,72,153,0.10),transparent_42%)]" />
            </div>

            <motion.div
                variants={sectionStaggerVariants}
                initial="initial"
                animate="animate"
                className="relative z-10 flex flex-col items-center px-8 text-center"
            >
                <motion.div variants={surfaceEntranceVariants} className="relative">
                    <motion.div
                        aria-hidden="true"
                        animate={
                            prefersReducedMotion
                                ? undefined
                                : {
                                    scale: [1, 1.04, 1],
                                    boxShadow: [
                                        '0 24px 80px rgba(99,102,241,0.22)',
                                        '0 28px 96px rgba(168,85,247,0.28)',
                                        '0 24px 80px rgba(99,102,241,0.22)',
                                    ],
                                }
                        }
                        transition={prefersReducedMotion ? undefined : { duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
                        className="absolute inset-0 rounded-[2rem]"
                    />
                    <div className="relative flex h-32 w-32 items-center justify-center rounded-[2rem] bg-linear-to-br from-indigo-600 via-violet-600 to-fuchsia-500 shadow-[0_22px_80px_rgba(99,102,241,0.28)]">
                        <motion.div
                            animate={prefersReducedMotion ? undefined : { rotate: [0, -6, 6, 0] }}
                            transition={prefersReducedMotion ? undefined : { duration: 5.2, repeat: Infinity, ease: MOTION_EASE }}
                        >
                            <Palette className="h-16 w-16 text-white" />
                        </motion.div>
                    </div>

                    <motion.div
                        variants={revealUpVariants}
                        className="absolute -right-3 top-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/92 shadow-lg shadow-indigo-200/60 backdrop-blur-sm dark:bg-gray-900/88 dark:shadow-black/20"
                    >
                        <Sparkles className="h-5 w-5 text-amber-500" />
                    </motion.div>
                    <motion.div
                        variants={revealUpVariants}
                        className="absolute -left-3 bottom-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/92 shadow-lg shadow-fuchsia-200/60 backdrop-blur-sm dark:bg-gray-900/88 dark:shadow-black/20"
                    >
                        <PenTool className="h-5 w-5 text-fuchsia-500" />
                    </motion.div>
                </motion.div>

                <motion.div variants={sectionStaggerVariants} className="mt-10 space-y-3">
                    <motion.h1
                        variants={revealUpVariants}
                        className="bg-linear-to-r from-indigo-700 via-violet-600 to-fuchsia-500 bg-clip-text text-3xl font-black tracking-tight text-transparent dark:from-indigo-300 dark:via-violet-300 dark:to-fuchsia-300"
                    >
                        AR Drawing Master
                    </motion.h1>
                    <motion.p
                        variants={revealUpVariants}
                        className="max-w-xs text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
                    >
                        Preparing your studio, templates, and AI tools for a smooth first sketch.
                    </motion.p>
                </motion.div>

                <motion.div
                    variants={revealUpVariants}
                    className="mt-8 w-56 overflow-hidden rounded-full bg-white/80 p-1 shadow-lg shadow-indigo-100/80 ring-1 ring-indigo-100/80 backdrop-blur-sm dark:bg-gray-900/80 dark:ring-gray-800"
                >
                    <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                        <motion.div
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={prefersReducedMotion ? { duration: 0.3 } : { duration: 1.9, ease: MOTION_EASE }}
                            style={{ originX: 0 }}
                            className="relative h-full rounded-full bg-linear-to-r from-indigo-500 via-violet-500 to-fuchsia-500"
                        >
                            {!prefersReducedMotion && (
                                <motion.div
                                    aria-hidden="true"
                                    animate={{ x: ['-100%', '120%'] }}
                                    transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
                                    className="absolute inset-y-0 left-0 w-16 bg-white/45 blur-md"
                                />
                            )}
                        </motion.div>
                    </div>
                </motion.div>

                <motion.div
                    variants={revealUpVariants}
                    className="mt-4 flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-300"
                >
                    <span>Loading studio</span>
                    <motion.span
                        animate={prefersReducedMotion ? undefined : { opacity: [0.35, 1, 0.35] }}
                        transition={prefersReducedMotion ? undefined : { duration: 1.5, repeat: Infinity }}
                    >
                        .
                    </motion.span>
                    <motion.span
                        animate={prefersReducedMotion ? undefined : { opacity: [0.35, 1, 0.35] }}
                        transition={prefersReducedMotion ? undefined : { duration: 1.5, repeat: Infinity, delay: 0.18 }}
                    >
                        .
                    </motion.span>
                    <motion.span
                        animate={prefersReducedMotion ? undefined : { opacity: [0.35, 1, 0.35] }}
                        transition={prefersReducedMotion ? undefined : { duration: 1.5, repeat: Infinity, delay: 0.36 }}
                    >
                        .
                    </motion.span>
                </motion.div>
            </motion.div>
        </div>
    );
};
